import { useQueries } from '@tanstack/react-query';
import axios from 'axios';
import { ZodError } from 'zod';
import chunk from 'lodash.chunk';
import uniq from 'lodash.uniq';
import { useMemo } from 'react';

import { logError } from '~/common/utils/log';
import type { GroupEntryView } from '~/services/marketplace/marketplace-service.schemas';
import { getGroupEntryViews } from '~/services/marketplace/marketplace-service.service.api';

import { STALE_TIME } from '~/common/constants/react-query.const';

import { GroupEntriesQueryKeys } from '../constants/query';

const BATCH_SIZE = 50;

export const useGroupEntryViews = (requestedEntryIds: readonly string[], userId: string) => {
  const entryIds = uniq(requestedEntryIds).sort();
  // Chunk from the sorted, deduped arguments only — never from a query-cache scan.
  const batches = userId ? chunk(entryIds, BATCH_SIZE) : [];

  const queries = useQueries({
    queries: batches.map((batch) => ({
      queryKey: GroupEntriesQueryKeys.entryViews(userId, batch),
      queryFn: async ({ signal }: { signal?: AbortSignal }) => {
        try {
          const views = await getGroupEntryViews(batch, signal);
          const batchIds = new Set(batch);
          return views.filter((view) => batchIds.has(view.entryId));
        } catch (error) {
          // Cancellation must rethrow: resolving empty would cache [] as a fresh success and blank the batch's cards.
          if (!axios.isCancel(error) && !(error instanceof ZodError)) logError(error);
          throw error;
        }
      },
      staleTime: STALE_TIME.CONTEST,
    })),
  });

  // `queries` is a fresh array every render, so the memo keys on this stable signature instead.
  const queriesSignature = `${queries.map((query) => query.dataUpdatedAt).join(',')}|${entryIds.join(',')}`;
  const views = useMemo(() => {
    const result = new Map<string, GroupEntryView>();
    queries.forEach((query) => {
      query.data?.forEach((view) => {
        result.set(view.entryId, view);
      });
    });
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queriesSignature]);

  return {
    views,
    isLoading: queries.some((query) => query.isInitialLoading),
    isError: queries.some((query) => query.isError),
  };
};
